import { useMemo, useState } from 'react';
import { Alert, Button, Group, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconArrowBackUp } from '@tabler/icons-react';
import type { PackConversionResponse } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { useAllLocations, usePackConversions, usePostPackConversion, useReverseAndClonePackConversion } from '../api';

function ConversionRowActions({ conversion }: { conversion: PackConversionResponse }) {
  const post = usePostPackConversion(conversion.id);
  const reverseClone = useReverseAndClonePackConversion(conversion);
  const [error, setError] = useState<string | null>(null);

  async function handleReverseClone() {
    setError(null);
    try {
      await reverseClone.mutateAsync();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this conversion');
    }
  }

  if (conversion.status === 'DRAFT') {
    return (
      <Button size="xs" loading={post.isPending} onClick={() => post.mutate()}>
        Post
      </Button>
    );
  }
  if (conversion.status === 'POSTED') {
    return (
      <Stack gap={2}>
        <ConfirmButton
          size="xs"
          color="red"
          variant="light"
          leftSection={<IconArrowBackUp size={14} />}
          loading={reverseClone.isPending}
          label="Reverse & clone"
          message={`Reverse pack conversion ${conversion.number}? Its stock movement will be backed out by a reversing ledger entry, and a new DRAFT copy will be created for you to redo.`}
          onConfirm={handleReverseClone}
        />
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
        {reverseClone.isSuccess && reverseClone.data && (
          <Text size="xs" c="green">
            Cloned as {reverseClone.data.number} (DRAFT)
          </Text>
        )}
      </Stack>
    );
  }
  return null;
}

/** Pack conversions register (BUILD_SPEC.md §11). Posted conversions offer
 * Reverse &amp; clone (plan.md QC fix #10). */
export function PackConversionsRegister() {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const conversionsQuery = usePackConversions(cursor);
  const locationsQuery = useAllLocations();
  const locationsById = useMemo(() => new Map((locationsQuery.data?.items ?? []).map((l) => [l.id, l])), [locationsQuery.data]);

  const rows = conversionsQuery.data?.items ?? [];

  return (
    <Stack gap="sm">
      <Title order={3}>Pack conversions</Title>
      {conversionsQuery.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load pack conversions.
        </Alert>
      )}
      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Number</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th># packs</Table.Th>
                <Table.Th>Occurred</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[...rows].reverse().map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>{c.number}</Table.Td>
                  <Table.Td>{locationsById.get(c.locationId)?.name ?? c.locationId}</Table.Td>
                  <Table.Td>{c.numPacks}</Table.Td>
                  <Table.Td>{new Date(c.occurredAt).toLocaleString()}</Table.Td>
                  <Table.Td>
                    <DocStatusBadge status={c.status} />
                  </Table.Td>
                  <Table.Td>
                    <ConversionRowActions conversion={c} />
                  </Table.Td>
                </Table.Tr>
              ))}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      No pack conversions yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
      {conversionsQuery.data?.nextCursor && (
        <Group justify="center">
          <Button variant="default" size="xs" onClick={() => setCursor(conversionsQuery.data?.nextCursor ?? undefined)}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}
